import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';
import {
    deleteEnvironment as deleteEnv,
    getEnvironmentFlows as loadEnvironmentFlows,
    getEnvironmentThemes as loadEnvironmentThemes,
    getEnvironmentVariables as loadEnvironmentVariables,
    getAllEnvironments as loadEnvironments,
    deleteEnvironmentVariable as removeEnvironmentVariable,
    saveEnvironment as save,
    saveEnvironmentVariable as saveEnvironmentVar,
} from '../../../sources/environments';
import {
    deleteRelease as deleteRel,
    deploy as deployApiCall,
    getRelease as loadRelease,
    getReleases as loadReleases,
    notifyRuntimesOnDeploy as notifyRuntimesOnDeployApiCall,
    removeFlowFromRelease as removeFlow,
    removeThemeFromRelease as removeTheme,
    rollback as rollbackApiCall,
} from '../../../sources/releases';
import translations from '../../../translations';

import type { ItemCollectionResponse } from '../../../types/api';
import type {
    Environment,
    EnvironmentFlow,
    EnvironmentTheme,
    EnvironmentVariable,
    GetEnvironmentFlowsRequest,
    GetEnvironmentThemesRequest,
    GetEnvironmentVariablesRequest,
} from '../../../types/environment';
import type { ReleaseAPI, ReleaseFilter } from '../../../types/releases';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';

type EnvironmentsContext = {
    getAllEnvironments: () => Promise<Environment[]>;
    getEnvironmentFlows: (
        params: GetEnvironmentFlowsRequest,
    ) => Promise<ItemCollectionResponse<EnvironmentFlow>> | null;
    environmentsLoading: boolean;
    environments: Environment[];
    saveEnvironment: (environment: Partial<Environment>) => Promise<void>;
    deleteEnvironment: (environmentId: string) => Promise<void>;
    getEnvironmentVariables: (
        environmentId: string,
        filter: GetEnvironmentVariablesRequest,
    ) => Promise<ItemCollectionResponse<EnvironmentVariable> | null>;
    saveEnvironmentVariable: (
        environmentId: string,
        environmentVariable: EnvironmentVariable,
    ) => Promise<void>;
    environmentVariables: EnvironmentVariable[];
    environmentVariablesLoading: boolean;
    deleteEnvironmentVariable: (
        environmentId: string,
        environmentVariableId: string,
    ) => Promise<void>;
    releases: ReleaseAPI[];
    getReleases: (filter: ReleaseFilter) => Promise<ReleaseAPI[]>;
    releasesLoading: boolean;
    getRelease: (id: string) => Promise<ReleaseAPI | null>;
    releaseLoading: boolean;
    deleteRelease: (releaseId: string) => Promise<void>;
    removeFlowFromRelease: (releaseId: string, flowId: string) => Promise<void>;
    removeThemeFromRelease: (
        releaseId: string,
        themeId: string,
        themeName: string,
    ) => Promise<void>;
    deploy: (releaseId: string, environmentId: string) => Promise<boolean>;
    notifyRuntimesOnDeploy: (
        messageId: string,
        releaseId: string,
        environmentId: string,
        isRollback: boolean,
    ) => Promise<boolean>;
    rollback: (releaseId: string, environmentId: string) => Promise<boolean>;
    setIsManagingEnvironments: React.Dispatch<React.SetStateAction<boolean>>;
    managingEnvironments: boolean;
    environmentFlowsLoading: boolean;
    getEnvironmentThemes: (
        params: GetEnvironmentThemesRequest,
    ) => Promise<ItemCollectionResponse<EnvironmentTheme>> | null;
    environmentThemesLoading: boolean;
};

type EnvironmentsProviderProps = {
    children?: ReactNode;
};

const Context = createContext<EnvironmentsContext | undefined>(undefined);

const EnvironmentsProvider = ({ children }: EnvironmentsProviderProps) => {
    const [environmentsLoading, setEnvironmentsLoading] = useState(false);
    const [environmentVariablesLoading, setEnvironmentVariablesLoading] = useState(false);
    const [environmentFlowsLoading, setEnvironmentFlowsLoading] = useState(false);
    const [environmentThemesLoading, setEnvironmentThemesLoading] = useState(false);
    const [releasesLoading, setReleasesLoading] = useState(false);
    const [releaseLoading, setReleaseLoading] = useState(false);
    const [managingEnvironments, setIsManagingEnvironments] = useState(false);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [releases, setReleases] = useState<ReleaseAPI[]>([]);
    const [environmentVariables, setEnvironmentVariables] = useState<EnvironmentVariable[]>([]);

    const dispatchNotification = useNotificationDispatch();

    const getAllEnvironments = useCallback(async () => {
        try {
            setEnvironmentsLoading(true);
            const results = await loadEnvironments();
            setEnvironments(results);
            return results;
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
            return [];
        } finally {
            setEnvironmentsLoading(false);
        }
    }, [dispatchNotification]);

    const saveEnvironment = useCallback(
        async (environment: Partial<Environment>) => {
            try {
                await save(environment);

                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: translations.ENVIRONMENT_save_success_message,
                    },
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const deleteEnvironment = useCallback(
        async (environmentId: string) => {
            try {
                await deleteEnv(environmentId);
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: translations.ENVIRONMENT_delete_success_message,
                    },
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const getEnvironmentFlows = useCallback(
        (params: GetEnvironmentFlowsRequest) => {
            try {
                setEnvironmentFlowsLoading(true);
                return loadEnvironmentFlows(params);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return null;
            } finally {
                setEnvironmentFlowsLoading(false);
            }
        },
        [dispatchNotification],
    );

    const getEnvironmentThemes = useCallback(
        (params: GetEnvironmentThemesRequest) => {
            try {
                setEnvironmentThemesLoading(true);
                return loadEnvironmentThemes(params);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return null;
            } finally {
                setEnvironmentThemesLoading(false);
            }
        },
        [dispatchNotification],
    );

    const getEnvironmentVariables = useCallback(
        async (environmentId: string, filter: GetEnvironmentVariablesRequest) => {
            try {
                setEnvironmentVariablesLoading(true);
                const response = await loadEnvironmentVariables(environmentId, filter);
                setEnvironmentVariables(response.items);
                return response;
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return null;
            } finally {
                setEnvironmentVariablesLoading(false);
            }
        },
        [dispatchNotification],
    );

    const saveEnvironmentVariable = useCallback(
        async (environmentId: string, environmentVariable: EnvironmentVariable) => {
            try {
                await saveEnvironmentVar(environmentId, environmentVariable);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const deleteEnvironmentVariable = useCallback(
        async (environmentId: string, environmentVariableId: string) => {
            try {
                await removeEnvironmentVariable(environmentId, environmentVariableId);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const getReleases = useCallback(
        async (filter: ReleaseFilter) => {
            try {
                setReleasesLoading(true);
                const results = await loadReleases(filter);
                setReleases(results);
                return results;
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return [];
            } finally {
                setReleasesLoading(false);
            }
        },
        [dispatchNotification],
    );

    const getRelease = useCallback(
        async (id: string) => {
            setReleaseLoading(true);
            try {
                return await loadRelease(id);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return null;
            } finally {
                setReleaseLoading(false);
            }
        },
        [dispatchNotification],
    );

    const deleteRelease = useCallback(
        async (releaseId: string) => {
            try {
                await deleteRel(releaseId);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const removeFlowFromRelease = useCallback(
        async (releaseId: string, flowId: string) => {
            try {
                await removeFlow(releaseId, flowId);

                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: translations.ENVIRONMENT_remove_item_success,
                    },
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const removeThemeFromRelease = useCallback(
        async (releaseId: string, themeId: string, themeName: string) => {
            try {
                await removeTheme(releaseId, themeId, themeName);
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: translations.ENVIRONMENT_remove_item_success,
                    },
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        },
        [dispatchNotification],
    );

    const deploy = useCallback(
        async (releaseId: string, environmentId: string) => {
            try {
                await deployApiCall(releaseId, environmentId);
                return true;
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return false;
            }
        },
        [dispatchNotification],
    );

    const notifyRuntimesOnDeploy = useCallback(
        async (
            messageId: string,
            releaseId: string,
            environmentId: string,
            isRollback: boolean,
        ) => {
            try {
                await notifyRuntimesOnDeployApiCall(
                    messageId,
                    releaseId,
                    environmentId,
                    isRollback,
                );
                return true;
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return false;
            }
        },
        [dispatchNotification],
    );

    const rollback = useCallback(
        async (releaseId: string, environmentId: string) => {
            try {
                await rollbackApiCall(releaseId, environmentId);
                return true;
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                return false;
            }
        },
        [dispatchNotification],
    );

    const contextValue: EnvironmentsContext = {
        environmentsLoading,
        environments,
        environmentVariables,
        environmentVariablesLoading,
        releases,
        releasesLoading,
        releaseLoading,
        managingEnvironments,
        environmentFlowsLoading,
        environmentThemesLoading,
        getEnvironmentFlows,
        getAllEnvironments,
        saveEnvironment,
        deleteEnvironment,
        getEnvironmentVariables,
        saveEnvironmentVariable,
        deleteEnvironmentVariable,
        getReleases,
        getRelease,
        deleteRelease,
        removeFlowFromRelease,
        removeThemeFromRelease,
        deploy,
        notifyRuntimesOnDeploy,
        rollback,
        setIsManagingEnvironments,
        getEnvironmentThemes,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useEnvironments = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error(translations.ENVIRONMENT_missing_context_provider_message);
    }
    return context;
};

export { EnvironmentsProvider, useEnvironments };
